import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, WordCloudRenderer } from "./index";
import { getInitials } from "../../util/general.util";
import { BulbIcon } from "../../assets/icons";
import { parseWordsList, skillsPillasCloudColors } from "../../util/wordcloud.util";
import { defaultPlatforms } from "../../util/workshop.util";

const TeamSkills = ({ skillsSubmissions = {}, users = [], isPdf = false, platform }) => {
  const { t } = useTranslation();
  const teamSkills = t("skills", { returnObjects: true });
  const {
    contentBox2: {
      tips: contentBox2Tips = [],
      topText: contentBox2TopText,
      bottomText: contentBox2BottomText
    } = {}
  } = teamSkills || {};

  const [validSkills, setValidSkills] = useState([]);
  const [skillsWords, setSkillsWords] = useState([]);
  const [showSkillBottomSection, setShowSkillBottomSection] = useState({
    firstPage: false,
    secondPage: false
  });

  const content = (part = "first") => {
    const { description, tips = [], topText, bottomText } = teamSkills?.content || {};

    const firstSevenSkills = [...validSkills]?.splice(0, 7);
    const remainingSkills = [...validSkills]?.splice(7);

    const targetSkills = isPdf
      ? part === "first"
        ? firstSevenSkills
        : remainingSkills
      : validSkills;

    return (
      <div className="w-full">
        {!isPdf && <p className="mb-4" dangerouslySetInnerHTML={{ __html: description }} />}

        {part === "first" && tips && tips?.length > 0 && (
          <div
            className={`rounded-lg border-2 border-gray-250/40 bg-orange-700 grid grid-cols-[auto_1fr] items-center gap-3 ${
              isPdf ? "px-4 py-2" : "p-4"
            }`}>
            <span className="hidden sm:block w-12 h-12 shrink-0">
              <BulbIcon />
            </span>
            <div>
              {topText && (
                <p className="mb-2 text-gray-250" dangerouslySetInnerHTML={{ __html: topText }} />
              )}
              <ul className={`list-disc pl-6 ${isPdf ? "text-xs" : ""}`}>
                {tips.map((tip, index) => (
                  <li
                    key={index}
                    className="text-[#575756]"
                    dangerouslySetInnerHTML={{ __html: tip }}
                  />
                ))}
              </ul>
              {bottomText && (
                <p
                  className="mt-2 text-gray-250"
                  dangerouslySetInnerHTML={{ __html: bottomText }}
                />
              )}
            </div>
          </div>
        )}

        {!isPdf && skillsWords?.length > 0 && (
          <div className={"w-full mt-4"}>
            <WordCloudRenderer
              id={"skills"}
              words={skillsWords}
              colorFunction={() => skillsPillasCloudColors.skillsWords}
              weightFactorFormula={(size) => size * 1.5}
              width={1500}
              height={1500}
            />
          </div>
        )}

        <div className={`w-full mt-4 ${isPdf ? "bg-orange-200 py-4 px-2 rounded-2xl" : ""}`}>
          <div
            className={`w-full ${
              isPdf ? " grid grid-cols-1 gap-1 !max-w-full" : "flex flex-col items-center"
            }`}>
            {targetSkills?.map(([userId, skills]) => {
              const user = users.find((u) => u.id.toString() === userId);
              return (
                <UserSkillTag
                  key={userId}
                  user={{
                    id: userId,
                    name: user?.name || userId,
                    skills: skills
                  }}
                  isPdf={isPdf}
                />
              );
            })}
          </div>
        </div>

        {(!isPdf || showSkillBottomSection.firstPage || showSkillBottomSection.secondPage) &&
          platform !== defaultPlatforms.mt &&
          platform !== defaultPlatforms.mtOnline && (
            <SkillsBottomSection
              isPdf={isPdf}
              contentBox2TopText={contentBox2TopText}
              contentBox2Tips={contentBox2Tips}
              contentBox2BottomText={contentBox2BottomText}
              autoHeight={showSkillBottomSection.firstPage || showSkillBottomSection.secondPage}
            />
          )}
      </div>
    );
  };

  useEffect(() => {
    if (Object.keys(skillsSubmissions)?.length > 0) {
      setValidSkills(
        Object.keys(skillsSubmissions).map((userId) => [userId, skillsSubmissions[userId]])
      );
    }

    try {
      const parsedSkillsWords = parseWordsList(skillsSubmissions, "skills");
      if (parsedSkillsWords?.length > 0) {
        setTimeout(() => {
          setSkillsWords(parsedSkillsWords);
        }, 500);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    const showSkillsBottomSectionFirstPage = isPdf && validSkills?.length < 6;
    const showSkillsBottomSectionSecondPage =
      isPdf && validSkills?.length > 7 && validSkills?.length < 13;

    setShowSkillBottomSection({
      firstPage: showSkillsBottomSectionFirstPage,
      secondPage: showSkillsBottomSectionSecondPage
    });
  }, [validSkills]);

  return (
    <>
      <div
        id="skills"
        className={
          isPdf ? "h-screen px-4 flex flex-col justify-start items-center overflow-hidden" : "mb-12"
        }>
        <Accordion
          title={teamSkills?.title}
          icon={teamSkills?.icon}
          content={content("first")}
          isPdf={isPdf}
        />
      </div>

      {isPdf && validSkills?.length > 7 && (
        <div
          id="skills"
          className={
            isPdf
              ? "h-screen px-4 flex flex-col justify-start items-center overflow-hidden"
              : "mb-12"
          }>
          <Accordion
            title={teamSkills?.title + ` (${t("skills.continued")})`}
            icon={teamSkills?.icon}
            content={content("second")}
            isPdf={isPdf}
          />
        </div>
      )}

      {/* Show in next page if there are more than 12 skills */}
      {isPdf && !showSkillBottomSection.firstPage && !showSkillBottomSection.secondPage && (
        <SkillsBottomSection
          isPdf={isPdf}
          contentBox2TopText={contentBox2TopText}
          contentBox2Tips={contentBox2Tips}
          contentBox2BottomText={contentBox2BottomText}
        />
      )}
    </>
  );
};

const SkillsBottomSection = ({
  isPdf,
  contentBox2TopText,
  contentBox2Tips,
  contentBox2BottomText,
  autoHeight = false
}) => {
  return (
    <div
      id="skills-bottom-section"
      className={
        isPdf
          ? `h-screen px-4 flex flex-col justify-start items-center overflow-hidden ${autoHeight ? "h-auto !px-0" : ""}`
          : ""
      }>
      {contentBox2Tips && contentBox2Tips?.length > 0 && (
        <div
          className={`rounded-lg border-2 border-gray-250/40 bg-orange-700 grid grid-cols-[auto_1fr] items-center gap-3 p-4 mt-4`}>
          <span className="hidden sm:block w-12 h-12 shrink-0">
            <BulbIcon />
          </span>
          <div>
            {contentBox2TopText && (
              <p
                className="mb-2 text-gray-250"
                dangerouslySetInnerHTML={{ __html: contentBox2TopText }}
              />
            )}
            {contentBox2Tips?.length > 0 && (
              <ul className={`list-disc pl-6 ${isPdf ? "text-xs" : ""}`}>
                {contentBox2Tips.map((tip, index) => (
                  <li
                    key={index}
                    className="text-[#575756]"
                    dangerouslySetInnerHTML={{ __html: tip }}
                  />
                ))}
              </ul>
            )}
            {contentBox2BottomText && (
              <p
                className="mt-2 text-gray-250"
                dangerouslySetInnerHTML={{ __html: contentBox2BottomText }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserSkillTag = ({ user, isPdf }) => {
  const initials = getInitials(user.name);

  return (
    <div
      className={`flex items-center space-x-4 ${!isPdf && "mb-4"} last:mb-0 w-full bg-white-100 rounded-lg p-2 border border-gray-250/40`}>
      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold text-white-100 flex-shrink-0">
        {initials}
      </div>
      <div className="flex-1">
        <div className={`font-semibold text-purple-200 ${isPdf ? "text-sm" : "text-md"}`}>
          {user.skills.map((skill, index) => (
            <p key={index}>{skill}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TeamSkills;
